import { claimExactKeys, getSessionIdentity, hasIdentity, TSessionIdentity } from "@/utils/jwt-claims";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";

/**
 * Why the app handed the session back to the center login. Recorded on every
 * bounce so a redirect loop reported to Sentry names the branch that caused it.
 */
export const OAUTH_LOGIN_REASONS = {
  loginPage: "login-page",
  unauthenticated: "unauthenticated",
  refreshFailedOn401: "refresh-failed-on-401",
  accountSwitched: "account-switched",
  socketForceLogout: "socket-force-logout",
} as const;

/** A 403 bounce carries the API error code (`forbidden:MATCH_ALREADY_DELETED`, ...). */
export type TOAuthLoginReason = (typeof OAUTH_LOGIN_REASONS)[keyof typeof OAUTH_LOGIN_REASONS] | `forbidden:${string}`;

/** Where the reported email / 組織ID came from — a bounce after `logout()` has no token left to read. */
type TIdentitySource = "token" | "remembered" | "cookie" | "unknown";

/** One recorded hand-off to the center login. */
type TOAuthBounceEntry = {
  at: number;
  reason: TOAuthLoginReason;
  /** Where the user was when the bounce fired — reveals a query param that keeps re-triggering it. */
  from: string;
  /** `iat` of the access token held at that moment. An unchanged value across bounces means the center keeps replaying the same (rejected) token. */
  tokenIat?: number;
  email?: string;
  externalId?: string;
};

type TOAuthBounceState = {
  /** Consecutive bounces before this one (0 = first of a burst). */
  n: number;
  at: number;
  trail: TOAuthBounceEntry[];
};

const BOUNCE_STORAGE_KEY = "match_oauth_bounce";
const IDENTITY_STORAGE_KEY = "match_oauth_identity";
/** Two bounces further apart than this are unrelated re-logins, not a loop. */
const BOUNCE_WINDOW_MS = 30_000;
/** Keep the trail small — it travels inside the Sentry payload. */
const MAX_TRAIL_ENTRIES = 10;

const readBounceState = (): TOAuthBounceState | null => {
  try {
    const raw = sessionStorage.getItem(BOUNCE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TOAuthBounceState;
    return typeof parsed?.n === "number" && typeof parsed?.at === "number" ? parsed : null;
  } catch {
    return null;
  }
};

const readRememberedIdentity = (): TSessionIdentity | null => {
  try {
    const raw = sessionStorage.getItem(IDENTITY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TSessionIdentity) : null;
  } catch {
    return null;
  }
};

/**
 * Stashes the signed-in identity so a later bounce can still be attributed.
 *
 * Most bounce branches run `logout()` before redirecting, which wipes the token
 * the email / 組織ID are claimed from — by report time there is nothing left to
 * read. Call this while the session is still alive (right after the OAuth
 * callback stores the cookies, and before any `logout()`).
 */
export const rememberOAuthIdentity = (identity: TSessionIdentity = getSessionIdentity()) => {
  if (!hasIdentity(identity)) return;
  try {
    sessionStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Storage unavailable (private mode / quota) — telemetry degrades, nothing else.
  }
};

/**
 * Best available identity for the bounce, preferring the live token claims and
 * falling back to what was stashed while the session was alive, then to the
 * cookies (which never carry `externalId`).
 */
const resolveIdentity = (): { identity: TSessionIdentity; source: TIdentitySource } => {
  const fromToken = getSessionIdentity();
  if (hasIdentity(fromToken)) return { identity: fromToken, source: "token" };

  const remembered = readRememberedIdentity();
  if (hasIdentity(remembered)) return { identity: remembered, source: "remembered" };

  const fromCookie: TSessionIdentity = {
    email: Cookies.get("email"),
    matchId: Cookies.get("matchId"),
    matchLicenseNumber: Cookies.get("matchLicenseNumber"),
  };
  if (hasIdentity(fromCookie)) return { identity: fromCookie, source: "cookie" };

  return { identity: {}, source: "unknown" };
};

/**
 * Cookie/token state at bounce time. Carries the identity (email + 組織ID) plus
 * the identifiers needed to tell the known loop causes apart — never the token
 * material itself.
 */
const getSessionSnapshot = () => {
  const accessToken = Cookies.get("match_token");
  const { iat, exp } = accessToken ? claimExactKeys(accessToken, ["iat", "exp"]) : {};
  const expSeconds = Number(exp);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const hasExp = Number.isFinite(expSeconds);
  const { identity, source } = resolveIdentity();

  return {
    email: identity.email,
    externalId: identity.externalId,
    sub: identity.sub,
    matchId: identity.matchId ?? Cookies.get("matchId"),
    matchLicenseNumber: identity.matchLicenseNumber ?? Cookies.get("matchLicenseNumber"),
    identitySource: source,
    userId: Cookies.get("userId"),
    isMatchUserCookie: Cookies.get("isMatchUser"),
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!Cookies.get("refresh_token"),
    accessTokenIat: Number.isFinite(Number(iat)) ? Number(iat) : undefined,
    accessTokenExp: hasExp ? expSeconds : undefined,
    accessTokenExpired: hasExp ? expSeconds <= nowSeconds : undefined,
    secondsUntilExpiry: hasExp ? expSeconds - nowSeconds : undefined,
  };
};

/**
 * Records a hand-off to the center login and returns how many bounces already
 * happened back-to-back (0 on the first one). The counter resets by itself once
 * `BOUNCE_WINDOW_MS` passes without a bounce, so a normal re-login never trips.
 *
 * Kept in sessionStorage on purpose: every bounce is a full page load, so
 * in-memory state and Sentry breadcrumbs are both wiped on each round-trip.
 */
export const recordOAuthBounce = (reason: TOAuthLoginReason): number => {
  const now = Date.now();
  const previous = readBounceState();
  const isSameBurst = !!previous && now - previous.at < BOUNCE_WINDOW_MS;
  const attempts = isSameBurst ? previous.n + 1 : 0;
  const snapshot = getSessionSnapshot();

  const entry: TOAuthBounceEntry = {
    at: now,
    reason,
    from: `${window.location.pathname}${window.location.search}`,
    tokenIat: snapshot.accessTokenIat,
    email: snapshot.email,
    externalId: snapshot.externalId,
  };
  const trail = [...(isSameBurst ? (previous.trail ?? []) : []), entry].slice(-MAX_TRAIL_ENTRIES);

  try {
    sessionStorage.setItem(BOUNCE_STORAGE_KEY, JSON.stringify({ n: attempts, at: now, trail } satisfies TOAuthBounceState));
  } catch {
    // Storage unavailable (private mode / quota) — the loop breaker degrades to a no-op.
  }

  return attempts;
};

export const clearOAuthBounceState = () => {
  try {
    sessionStorage.removeItem(BOUNCE_STORAGE_KEY);
  } catch {
    // Nothing to recover from — clearing is best-effort.
  }
};

/**
 * Reports a match <-> center redirect loop. Fingerprinted per reason so each
 * cause (revoked token, forbidden account state, mismatched query param) lands
 * in its own Sentry issue instead of one undiagnosable pile.
 *
 * The affected account travels as the Sentry `user` (email + 組織ID) so support
 * can look the report up by the same 組織ID the user logs in with.
 */
export const reportOAuthBounceLoop = (reason: TOAuthLoginReason, attempts: number) => {
  try {
    const session = getSessionSnapshot();
    const trail = readBounceState()?.trail ?? [];
    const tokenIats = trail.map((item) => item.tokenIat);
    // The center re-minting the same token is the signature of a revoked/stale handoff.
    const isSameTokenReplayed = tokenIats.length > 1 && tokenIats.every((iat) => iat !== undefined && iat === tokenIats[0]);

    Sentry.captureMessage(`OAuth redirect loop between match and center (${reason})`, {
      level: "error",
      user: { id: session.sub, email: session.email, username: session.externalId },
      tags: {
        feature: "oauth-login",
        oauthBounceReason: reason,
        oauthExternalId: session.externalId ?? "unknown",
        oauthIdentitySource: session.identitySource,
        oauthSameTokenReplayed: String(isSameTokenReplayed),
      },
      extra: {
        attempts,
        email: session.email,
        externalId: session.externalId,
        reasons: trail.map((item) => item.reason),
        trail,
        session,
        centerUrl: process.env.REACT_APP_CENTER_URL,
        currentUrl: window.location.href,
      },
      fingerprint: ["oauth-bounce-loop", reason],
    });
  } catch {
    // Telemetry is best-effort only — it must never block the redirect breaker.
  }
};
